import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TradeType, FixingReference, FixingOffset, SettlementType } from '../../types/tradeBooking';
import { Currency } from '../../types/currency';

export interface FixingExpiryConvention {
  id: string;
  tradeType: TradeType;
  currencyPair: string;
  defaultFixingReference: FixingReference;
  defaultFixingOffset: FixingOffset;
  defaultSettlementType: SettlementType;
  defaultSettlementCurrency: string;
  hasOverride: boolean;
  overrideFixingReference?: FixingReference;
  overrideFixingOffset?: FixingOffset;
  overrideSettlementType?: SettlementType;
  overrideSettlementCurrency?: string;
}

interface FixingExpirySettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  conventions: FixingExpiryConvention[];
  onSave: (conventions: FixingExpiryConvention[]) => void;
  currencies: Currency[];
}

const FixingExpirySettingsModal: React.FC<FixingExpirySettingsModalProps> = ({
  isOpen,
  onClose,
  conventions,
  onSave,
  currencies
}) => {
  const { t } = useTranslation();
  const [localConventions, setLocalConventions] = useState<FixingExpiryConvention[]>(conventions);

  // Sync local state when conventions prop changes
  React.useEffect(() => {
    setLocalConventions(conventions);
  }, [conventions]);

  // Filter out CLF as it's not a settleable currency
  const settleableCurrencies = currencies.filter(c => c.code !== 'CLF');

  if (!isOpen) return null;

  const handleToggleOverride = (id: string) => {
    setLocalConventions(prev => prev.map(conv => {
      if (conv.id !== id) return conv;

      const newOverrideState = !conv.hasOverride;

      // If enabling override, initialize override values with defaults
      if (newOverrideState) {
        return {
          ...conv,
          hasOverride: true,
          overrideFixingReference: conv.overrideFixingReference || conv.defaultFixingReference,
          overrideFixingOffset: conv.overrideFixingOffset || conv.defaultFixingOffset,
          overrideSettlementType: conv.overrideSettlementType || conv.defaultSettlementType,
          overrideSettlementCurrency: conv.overrideSettlementCurrency !== undefined
            ? conv.overrideSettlementCurrency
            : conv.defaultSettlementCurrency,
        };
      } else {
        // If disabling override, just toggle the flag
        return { ...conv, hasOverride: false };
      }
    }));
  };

  const handleDefaultChange = (
    id: string,
    field: keyof FixingExpiryConvention,
    value: any
  ) => {
    setLocalConventions(prev => prev.map(conv =>
      conv.id === id ? { ...conv, [field]: value } : conv
    ));
  };

  const handleOverrideChange = (
    id: string,
    field: keyof FixingExpiryConvention,
    value: any
  ) => {
    setLocalConventions(prev => prev.map(conv => {
      if (conv.id !== id) return conv;

      const updated = { ...conv, [field]: value };

      // If Settlement Type changed to Entrega Física, set Settlement Currency to empty
      if (field === 'overrideSettlementType' && value === SettlementType.ENTREGA_FISICA) {
        updated.overrideSettlementCurrency = '';
      }

      return updated;
    }));
  };

  const handleSave = () => {
    onSave(localConventions);
  };

  const handleCancel = () => {
    setLocalConventions(conventions);
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={handleCancel}>
      <div className="modal-dialog fixing-expiry-settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('tradeBooking.fixingExpiry.settingsTitle')}</h2>
        </div>

        <div className="modal-body dates-modal-body">
          <div className="dates-modal-header">
            <p>{t('tradeBooking.settings.fixingDescription')}</p>
          </div>

          <div className="dates-table-container">
            <table className="fixing-expiry-table">
              <thead>
                <tr>
                  <th rowSpan={2} className="main-header" style={{width: '8%'}}>{t('tradeBooking.tradeType.label')}</th>
                  <th rowSpan={2} className="main-header" style={{width: '10%'}}>{t('tradeBooking.dates.currencyPair')}</th>
                  <th colSpan={4} className="main-header" style={{width: '36%'}}>
                    {t('tradeBooking.dates.defaultConventions')}
                  </th>
                  <th rowSpan={2} className="main-header toggle-header" style={{width: '6%'}}>{t('tradeBooking.dates.override')}</th>
                  <th colSpan={4} className="main-header" style={{width: '36%'}}>
                    {t('tradeBooking.dates.userOverrides')}
                  </th>
                </tr>
                <tr>
                  <th className="field-header date-col">{t('tradeBooking.fixingExpiry.fixingReference')}</th>
                  <th className="field-header date-col">{t('tradeBooking.fixingExpiry.fixingOffset')}</th>
                  <th className="field-header date-col">{t('tradeBooking.fixingExpiry.settlementType')}</th>
                  <th className="field-header date-col">{t('tradeBooking.fixingExpiry.settlementCurrency')}</th>
                  <th className="field-header date-col override-col">{t('tradeBooking.fixingExpiry.fixingReference')}</th>
                  <th className="field-header date-col override-col">{t('tradeBooking.fixingExpiry.fixingOffset')}</th>
                  <th className="field-header date-col override-col">{t('tradeBooking.fixingExpiry.settlementType')}</th>
                  <th className="field-header date-col override-col">{t('tradeBooking.fixingExpiry.settlementCurrency')}</th>
                </tr>
              </thead>
            <tbody>
              {localConventions.map(convention => (
                <tr key={convention.id}>
                  <td className="readonly-cell">
                    {t(`tradeBooking.tradeType.${convention.tradeType.toLowerCase()}`)}
                  </td>
                  <td className="readonly-cell">
                    {convention.currencyPair === 'All Others'
                      ? t('tradeBooking.dates.allOthers')
                      : convention.currencyPair}
                  </td>

                  {/* Default Conventions */}
                  <td className="readonly-cell">
                    {t(`tradeBooking.fixingReferences.${convention.defaultFixingReference}`)}
                  </td>
                  <td className="readonly-cell">{convention.defaultFixingOffset}</td>
                  <td className="readonly-cell">
                    {t(`tradeBooking.settlementTypes.${convention.defaultSettlementType}`)}
                  </td>
                  <td className="readonly-cell">
                    {convention.defaultSettlementCurrency || 'N/A'}
                  </td>

                  {/* Override Toggle */}
                  <td className="toggle-cell">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={convention.hasOverride}
                        onChange={() => handleToggleOverride(convention.id)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </td>

                  {/* Override Values */}
                  <td>
                    <select
                      className="convention-select"
                      value={convention.overrideFixingReference || convention.defaultFixingReference}
                      onChange={(e) => handleOverrideChange(convention.id, 'overrideFixingReference', e.target.value as FixingReference)}
                      disabled={!convention.hasOverride}
                    >
                      {Object.values(FixingReference).filter(ref => ref !== FixingReference.NA).map((ref) => (
                        <option key={ref} value={ref}>
                          {t(`tradeBooking.fixingReferences.${ref}`)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select
                      className="convention-select"
                      value={convention.overrideFixingOffset || convention.defaultFixingOffset}
                      onChange={(e) => handleOverrideChange(convention.id, 'overrideFixingOffset', e.target.value as FixingOffset)}
                      disabled={!convention.hasOverride}
                    >
                      {Object.values(FixingOffset).filter(offset => offset !== FixingOffset.NA).map((offset) => (
                        <option key={offset} value={offset}>
                          {offset}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select
                      className="convention-select"
                      value={convention.overrideSettlementType || convention.defaultSettlementType}
                      onChange={(e) => handleOverrideChange(convention.id, 'overrideSettlementType', e.target.value as SettlementType)}
                      disabled={!convention.hasOverride}
                    >
                      {Object.values(SettlementType).map((type) => (
                        <option key={type} value={type}>
                          {t(`tradeBooking.settlementTypes.${type}`)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select
                      className="convention-select"
                      value={convention.overrideSettlementCurrency || convention.defaultSettlementCurrency || ''}
                      onChange={(e) => handleOverrideChange(convention.id, 'overrideSettlementCurrency', e.target.value)}
                      disabled={
                        !convention.hasOverride ||
                        (convention.overrideSettlementType || convention.defaultSettlementType) === SettlementType.ENTREGA_FISICA
                      }
                    >
                      <option value="">N/A</option>
                      {settleableCurrencies.map((curr) => (
                        <option key={curr.code} value={curr.code}>
                          {curr.code}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={handleCancel}>
            {t('tradeBooking.settings.cancel')}
          </button>
          <button type="button" className="btn btn-primary" onClick={handleSave}>
            {t('tradeBooking.settings.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FixingExpirySettingsModal;
